import { DropdownMenu as KobalteDropdownMenu } from "@kobalte/core/dropdown-menu"
import { createMemo, For, type ParentProps, Show } from "solid-js"
import {
  dropdownContentClass,
  dropdownItemLabelClass,
  dropdownItemRecipe,
} from "@/designSystem/components/dropdown-menu.css"
import { Icon } from "@/designSystem/components/icon"
import { useI18nContext } from "@/i18n/i18n-solid"

type CodeMenuProps = ParentProps<{
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  triggerClass: string
  triggerLabel: string
  onTriggerPointerDown: () => void
  onTriggerClick: (event: MouseEvent) => void
  onTriggerKeyDown: (event: KeyboardEvent) => void
  onTriggerDragStart: (event: DragEvent) => void
  onTriggerDragEnd: () => void
  language: string
  onUpdateLanguage: (language: string) => void
  onDelete: () => void
}>

interface CodeLanguageOption {
  label: string
  value: string
}

export function CodeMenu(props: CodeMenuProps) {
  const { LL } = useI18nContext()

  const languageOptions = createMemo<CodeLanguageOption[]>(() => [
    { value: "text", label: "Plain text" },
    { value: "javascript", label: "JavaScript" },
    { value: "typescript", label: "TypeScript" },
    { value: "tsx", label: "TSX" },
    { value: "python", label: "Python" },
    { value: "go", label: "Go" },
    { value: "rust", label: "Rust" },
    { value: "java", label: "Java" },
    { value: "php", label: "PHP" },
    { value: "sql", label: "SQL" },
    { value: "json", label: "JSON" },
    { value: "html", label: "HTML" },
    { value: "css", label: "CSS" },
    { value: "yaml", label: "YAML" },
    { value: "markdown", label: "Markdown" },
    { value: "c++", label: "C++" },
  ])

  const currentLanguage = createMemo(() => props.language.toLowerCase())

  return (
    <KobalteDropdownMenu open={props.isOpen} onOpenChange={props.onOpenChange}>
      <KobalteDropdownMenu.Trigger
        class={props.triggerClass}
        aria-label={props.triggerLabel}
        data-block-node-menu-trigger="true"
        draggable="true"
        onPointerDown={props.onTriggerPointerDown}
        onClick={props.onTriggerClick}
        onKeyDown={props.onTriggerKeyDown}
        onDragStart={props.onTriggerDragStart}
        onDragEnd={props.onTriggerDragEnd}
      >
        {props.children}
      </KobalteDropdownMenu.Trigger>
      <KobalteDropdownMenu.Portal>
        <KobalteDropdownMenu.Content class={dropdownContentClass}>
          <KobalteDropdownMenu.Sub>
            <KobalteDropdownMenu.SubTrigger
              class={dropdownItemRecipe({ hue: "sand" })}
            >
              <Icon name="languages" size="18" />
              <span class={dropdownItemLabelClass}>
                {LL().editor.language()}
              </span>
              <Icon name="chevron-right" size="18" />
            </KobalteDropdownMenu.SubTrigger>
            <KobalteDropdownMenu.Portal>
              <KobalteDropdownMenu.SubContent class={dropdownContentClass}>
                <For each={languageOptions()}>
                  {option => (
                    <KobalteDropdownMenu.Item
                      class={dropdownItemRecipe({ hue: "sand" })}
                      onSelect={() => props.onUpdateLanguage(option.value)}
                    >
                      <Show when={currentLanguage() === option.value}>
                        <Icon name="check" size="18" />
                      </Show>
                      <span class={dropdownItemLabelClass}>{option.label}</span>
                    </KobalteDropdownMenu.Item>
                  )}
                </For>
              </KobalteDropdownMenu.SubContent>
            </KobalteDropdownMenu.Portal>
          </KobalteDropdownMenu.Sub>
          <KobalteDropdownMenu.Item
            class={dropdownItemRecipe({ hue: "rose" })}
            onSelect={props.onDelete}
          >
            <Icon name="trash-2" size="18" />
            <span class={dropdownItemLabelClass}>{LL().common.delete()}</span>
          </KobalteDropdownMenu.Item>
        </KobalteDropdownMenu.Content>
      </KobalteDropdownMenu.Portal>
    </KobalteDropdownMenu>
  )
}
